import React from 'react';
import { motion } from 'framer-motion';
import { MagicCard } from '@/components/lib/magicui/MagicCard';
import { TracingBeam } from '@/components/lib/aceternity/TracingBeam';

interface ProtocolStep {
  stepNumber: number;
  label: string;
  title: string;
  body: string;
}

export default function AboutProtocol() {
  // 5-step process data
  const steps: ProtocolStep[] = [
    {
      stepNumber: 1,
      label: 'STEP 1',
      title: 'Domain Alignment',
      body: 'I start by clarifying what "winning" looks like for this domain in your market — who it should attract, what it should sell, and how it should position you.',
    },
    {
      stepNumber: 2,
      label: 'STEP 2',
      title: 'Strategy Architecture',
      body: 'I map your offers, pricing, and sales path so your site has a clear job: turn the right visitors into paying clients, not just page views.',
    },
    {
      stepNumber: 3,
      label: 'STEP 3',
      title: 'Brand System',
      body: 'I lock in the positioning, messaging, logo, and visual system so your brand feels premium, coherent, and unmistakably "you."',
    },
    {
      stepNumber: 4,
      label: 'STEP 4',
      title: 'Website Engine',
      body: 'I design and build a conversion-first site that showcases your expertise, removes friction, and makes taking the next step feel easy.',
    },
    {
      stepNumber: 5,
      label: 'STEP 5',
      title: 'The System That Runs It',
      body: 'We launch with a workspace the AI can see your business through, plus automations that answer enquiries, follow up leads and handle the admin. The domain starts filling your calendar without you holding it up.',
    },
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <motion.section
      id="attain-protocol"
      aria-label="The Attain Protocol 5-step process"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full py-20 md:py-24 bg-slate-950 overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute top-0 left-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" />
      <div className="pointer-events-none absolute bottom-0 right-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" />
      
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Top heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mb-16 md:mb-20"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-400 uppercase mb-4">
                THE ATTAIN PROTOCOL
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50 mb-6">
                A 5-step path from "great domain" to "fully booked."
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
                When you bring me a premium domain we don't wing it. We follow a protocol that turns the name into a functioning business: strategy and brand first, then the site, then the AI system that runs it day to day.
              </p>
            </motion.div>
          </motion.div>

          {/* 5-step process tiles with TracingBeam */}
          <div className="relative">
            <TracingBeam className="hidden md:block">
              <motion.ol
                variants={gridVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5 mb-16"
              >
                {steps.map((step) => (
                  <motion.li key={step.stepNumber} variants={cardVariants}>
                    <MagicCard className="h-full">
                      <div className="p-6 h-full flex flex-col">
                        <span className="text-xs font-semibold text-[#0000FF] tracking-wide uppercase mb-3">
                          {step.label}
                        </span>
                        <h3 className="text-lg md:text-xl font-semibold text-slate-100 mb-3">
                          {step.title}
                        </h3>
                        <p className="text-sm md:text-base text-slate-300 leading-relaxed">
                          {step.body}
                        </p>
                      </div>
                    </MagicCard>
                  </motion.li>
                ))}
              </motion.ol>
            </TracingBeam>

            {/* Mobile version without TracingBeam */}
            <motion.ol
              variants={gridVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16"
            >
              {steps.map((step) => (
                <motion.li key={step.stepNumber} variants={cardVariants}>
                  <MagicCard className="h-full">
                    <div className="p-6 h-full flex flex-col">
                      <span className="text-xs font-semibold text-[#0000FF] tracking-wide uppercase mb-3">
                        {step.label}
                      </span>
                      <h3 className="text-lg md:text-xl font-semibold text-slate-100 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-sm md:text-base text-slate-300 leading-relaxed">
                        {step.body}
                      </p>
                    </div>
                  </MagicCard>
                </motion.li>
              ))}
            </motion.ol>
          </div>

          {/* "Who this protocol is built for" text block */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wide mb-4">
              Who this protocol is built for
            </h3>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed">
              This is for expert service providers, boutique agencies, and domain owners who care
              about long-term value — not just getting something "live." If you want your domain to
              behave like an asset instead of a business card, this is the path we'll walk together.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

